import { Button, Card, CardBody, Col, Container, FormGroup, Label, Row } from "reactstrap";
import { useTranslation } from "react-i18next";
import { ErrorMessage, Field, Form, Formik, FieldArray, FormikErrors } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Trash2, Edit } from "react-feather";
import Breadcrumbs from "../../../common-elements/breadcrumbs/Breadcrumbs";
import { useEffect, useState, useCallback, useMemo } from "react";
import { UserListTableData } from "../../../data/users/UsersList";
import { Image } from "../../../AbstractElements";
import { dynamicImage } from "../../../service";
import { UserListTableDataColumnType } from "../../../types/users/UserTypes";
import Select from "react-select";
import { showWarningAlert } from "../../../utils/showWarningAlert";
import { UncontrolledTooltip } from "reactstrap";
import { InvoiceData } from "./InvoiceList";
import { invoiceValidationSchema } from "./add-invoice/InvoiceAddValidation";
import { FormValues, InvoiceItem } from "../../../types/invoice/AddInvoice";
import { EditableItemRow } from "./add-invoice/EditableItemRow";
import { DisplayItemRow } from "./add-invoice/DisplayItemRow";
import { InvoiceSummary } from "./add-invoice/InvoiceSummary";

// Define types for InvoiceData
interface InvoiceDataItem {
  id: string;
  item: string;
  quantity: string;
  price: string;
  tax: string;
  discount?: string;
  total: number;
}

interface InvoiceDataType {
  id: number;
  organizationName: string;
  invoiceItems: InvoiceDataItem[];
  image?: string;
}

export const taxOptions = [
  { value: "0", label: "0% (No Tax)" },
  { value: "5", label: "5% GST" },
  { value: "12", label: "12% GST" },
  { value: "15", label: "15% GST" },
  { value: "18", label: "18% GST" },
];

const AddInvoiceSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState("");
  const [initialValues, setInitialValues] = useState<FormValues>({
    organizationName: "",
    invoiceItems: [],
  });

  const generateId = useCallback(() => Math.random().toString(36).substr(2, 9), []);

  const organizationData = useMemo(() => {
    const data = UserListTableData as UserListTableDataColumnType[];
    const organizations = Array.from(new Set(data.map(u => u.organizationname || "").filter(Boolean)));
    const logoMap = data.reduce((acc, user) => {
      if (user.organizationname && user.logo) acc[user.organizationname] = user.logo;
      return acc;
    }, {} as Record<string, string>);
    
    return { organizations, logoMap };
  }, []);

  const organizationOptions = useMemo(() => 
    organizationData.organizations.map(org => ({
      value: org,
      label: org,
      logo: organizationData.logoMap[org] || "",
    })), 
    [organizationData]
  );

  useEffect(() => {

    const invoice = id ?InvoiceData.find(
      (user: any) => user.id.toString() === id
    )  : null;
    setIsEditMode(!!id && !!invoice);
    
    setInitialValues({
      organizationName: invoice?.organizationName || "",
      invoiceItems: invoice?.invoiceItems?.map(item => ({
        id: item.id,
        item: item.item,
        quantity: item.quantity,
        taxRate: item.tax.replace("%", ""),
        price: item.price,
        discount: item.discount || "0",
        total: item.total,
        isEditing: true,
      })) || [],
    });
    
    setSelectedOrg(invoice?.organizationName || "");
    if (invoice?.organizationName && invoice.image) {
      organizationData.logoMap[invoice.organizationName] = invoice.image;
    }
  }, [id, organizationData.logoMap]);

  const calculateTotals = useCallback((items: InvoiceItem[]) => {
    const subtotal = items.reduce((acc, item) => acc + Number(item.quantity || 0) * Number(item.price || 0), 0);
    const totalDiscount = items.reduce((acc, item) => acc + Number(item.discount || 0), 0);
    const subtotalAfterDiscount = subtotal - totalDiscount;
    const totalGst = items.reduce((acc, item) => {
      const itemSubtotal = Number(item.quantity || 0) * Number(item.price || 0);
      const itemDiscount = Number(item.discount || 0);
      const taxableAmount = itemSubtotal - itemDiscount;
      return acc + taxableAmount * (Number(item.taxRate || 0) / 100);
    }, 0);
    
    return { 
      subtotal, 
      totalDiscount, 
      subtotalAfterDiscount, 
      totalGst, 
      grandTotal: subtotalAfterDiscount + totalGst,
      discount: totalDiscount // Added to match InvoiceSummary expected type
    };
  }, []);

  const focusNextField = useCallback((currentRow: HTMLElement, itemIdx: number, fieldName: string) => {
    const fieldOrder = ["item", "quantity", "price", "taxRate", "discount"] as const;
    const currentFieldIndex = fieldOrder.indexOf(fieldName as typeof fieldOrder[number]);
    if (currentFieldIndex >= fieldOrder.length - 1) return false;

    const nextField = fieldOrder[currentFieldIndex + 1];
    const selector = nextField === "taxRate" 
      ? `select[name="invoiceItems[${itemIdx}].${nextField}"]`
      : `input[name="invoiceItems[${itemIdx}].${nextField}"]`;
    
    const nextInput = currentRow.querySelector(selector) as HTMLInputElement | HTMLSelectElement;
    if (nextInput) nextInput.focus();
    return true;
  }, []);

  const validateAndFocusError = useCallback((
    itemIdx: number, 
    item: InvoiceItem, 
    errors: FormikErrors<InvoiceItem>, 
    row: HTMLElement
  ) => {
    const fields = ["item", "quantity", "price", "taxRate"] as const;
    for (const field of fields) {
      if (errors[field] || !item[field].trim()) {
        const selector = field === "taxRate" 
          ? `select[name="invoiceItems[${itemIdx}].${field}"]`
          : `input[name="invoiceItems[${itemIdx}].${field}"]`;
        const input = row.querySelector(selector) as HTMLInputElement | HTMLSelectElement;
        if (input) input.focus();
        return true;
      }
    }
    return false;
  }, []);

  const handleKeyPress = useCallback((
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
    itemIdx: number,
    fieldName: string,
    values: FormValues,
    errors: FormikErrors<FormValues>,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    validateField: (field: string) => void,
    push: (obj: InvoiceItem) => void
  ) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;
    e.preventDefault();

    const currentRow = (e.target as HTMLInputElement).closest(".range-card") as HTMLElement;
    if (!currentRow) return;

    if (focusNextField(currentRow, itemIdx, fieldName)) return;

    const currentItem = values.invoiceItems[itemIdx];
    const itemErrors = Array.isArray(errors.invoiceItems) && errors.invoiceItems[itemIdx]
      ? errors.invoiceItems[itemIdx] as FormikErrors<InvoiceItem>
      : {};

    const fields = ["item", "quantity", "taxRate", "price"] as const;
    fields.forEach(field => {
      setFieldTouched(`invoiceItems[${itemIdx}].${field}`, true, true);
      validateField(`invoiceItems[${itemIdx}].${field}`);
    });

    const allFieldsFilled = fields.every(field => currentItem[field].trim());
    const noErrors = fields.every(field => !itemErrors[field]);

    if (!isEditMode && allFieldsFilled && noErrors) {
      push({ id: generateId(), item: "", quantity: "1", taxRate: "0", price: "", discount: "0", total: 0, isEditing: true });
      setTimeout(() => {
        const newRow = document.querySelector(`[name="invoiceItems[${values.invoiceItems.length}].item"]`) as HTMLInputElement;
        if (newRow) newRow.focus();
      }, 100);
    } else if (isEditMode) {
      const nextRowInput = document.querySelector(`input[name="invoiceItems[${itemIdx + 1}].item"]`) as HTMLInputElement;
      (nextRowInput || currentRow.querySelector(`input[name="invoiceItems[${itemIdx}].item"]`))?.focus();
    } else {
      validateAndFocusError(itemIdx, currentItem, itemErrors, currentRow);
    }
  }, [isEditMode, generateId, focusNextField, validateAndFocusError]);

  const handleAddItem = useCallback(async (
    values: FormValues,
    errors: FormikErrors<FormValues>,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    validateField: (field: string) => void,
    push: (obj: InvoiceItem) => void
  ) => {
    const lastItem = values.invoiceItems[values.invoiceItems.length - 1];
    if (!lastItem) {
      push({ id: generateId(), item: "", quantity: "1", taxRate: "0", price: "", discount: "0", total: 0, isEditing: true });
      return focusNewRow(values.invoiceItems.length);
    }

    const itemErrors = Array.isArray(errors.invoiceItems) && errors.invoiceItems[values.invoiceItems.length - 1]
      ? errors.invoiceItems[values.invoiceItems.length - 1] as FormikErrors<InvoiceItem>
      : {};

    const fields = ["item", "quantity", "taxRate", "price"] as const;
    await Promise.all(fields.map(field => 
      Promise.all([
        setFieldTouched(`invoiceItems[${values.invoiceItems.length - 1}].${field}`, true, true),
        validateField(`invoiceItems[${values.invoiceItems.length - 1}].${field}`)
      ])
    ));

    const allFieldsFilled = fields.every(field => lastItem[field].trim());
    const noErrors = fields.every(field => !itemErrors[field]);

    if (allFieldsFilled && noErrors) {
      push({ id: generateId(), item: "", quantity: "1", taxRate: "0", price: "", discount: "0", total: 0, isEditing: true });
      focusNewRow(values.invoiceItems.length);
    } else {
      const lastRow = document.querySelector(`.range-card:nth-last-child(2)`) as HTMLElement;
      if (lastRow) validateAndFocusError(values.invoiceItems.length - 1, lastItem, itemErrors, lastRow);
    }
  }, [generateId, validateAndFocusError]);

  const focusNewRow = useCallback((index: number) => {
    setTimeout(() => {
      const newRow = document.querySelector(`[name="invoiceItems[${index}].item"]`) as HTMLInputElement;
      if (newRow) newRow.focus();
    }, 100);
  }, []);

  const handleSaveAllItems = useCallback(async (
    values: FormValues,
    errors: FormikErrors<FormValues>,
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void,
    validateField: (field: string) => void,
    replace: (index: number, obj: InvoiceItem) => void
  ) => {
    const fields = ["item", "quantity", "taxRate", "price"] as const;
    const hasErrors = await Promise.all(
      values.invoiceItems.map(async (_, i) => {
        await Promise.all(fields.map(field => 
          Promise.all([
            setFieldTouched(`invoiceItems[${i}].${field}`, true, true),
            validateField(`invoiceItems[${i}].${field}`)
          ])
        ));
        return Array.isArray(errors.invoiceItems) && errors.invoiceItems[i] && 
               Object.keys(errors.invoiceItems[i] as FormikErrors<InvoiceItem>).length > 0;
      })
    ).then(results => results.some(Boolean));

    if (!hasErrors) {
      values.invoiceItems.forEach((item, index) => {
        const subtotal = Number(item.quantity) * Number(item.price);
        const discount = Number(item.discount || 0);
        replace(index, {
          ...item,
          item: String(item.item),
          quantity: String(item.quantity),
          taxRate: String(item.taxRate),
          price: String(item.price),
          discount: String(item.discount || "0"),
          total: subtotal - discount,
          isEditing: false,
        });
      });
    }
  }, []);

  const handleSubmit = useCallback(async (values: FormValues) => {
    await showWarningAlert({
      icon: "success",
      title: t("Success"),
      text: t("Invoice details saved successfully"),
      confirmButtonColor: "var(--theme-default)",
    });
    navigate(`${process.env.PUBLIC_URL}/billingpayments/invoice`);
  }, [navigate, t]);

  return (
    <div>
      <Breadcrumbs
        mainTitle={isEditMode ? t("Edit Invoice") : t("Add Invoice")}
        parents={[{ label: t("BillsPayment"), link: `${process.env.PUBLIC_URL}/billingpayments/invoice` }]}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={invoiceValidationSchema}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ setFieldValue, values, errors, setFieldTouched, validateField }) => {
                    const totals = calculateTotals(values.invoiceItems);

                    return (
                      <Form className="theme-form">
                        <Row>
                          <Col>
                            <FormGroup>
                              <Label>{t("OrganizationName")}</Label>
                              <Select
                                options={organizationOptions}
                                placeholder={t("Select Organization")}
                                className="basic-single-select"
                                classNamePrefix="select"
                                isSearchable
                                isClearable
                                value={selectedOrg ? { value: selectedOrg, label: selectedOrg, logo: organizationData.logoMap[selectedOrg] || "" } : null}
                                onChange={(option) => {
                                  const orgName = option?.value || "";
                                  setSelectedOrg(orgName);
                                  setFieldValue("organizationName", orgName);
                                  setFieldValue("invoiceItems", orgName ? [{ id: generateId(), item: "", quantity: "1", taxRate: "0", price: "", discount: "0", total: 0, isEditing: true }] : []);
                                }}
                                formatOptionLabel={(option: any) => (
                                  <div className="d-flex align-items-center">
                                    {option.logo && (
                                      <Image
                                        src={dynamicImage(option.logo)}
                                        alt={option.label}
                                        style={{ width: 30, height: 30, borderRadius: "50%", marginRight: 8 }}
                                      />
                                    )}
                                    <span>{option.label}</span>
                                  </div>
                                )}
                              />
                              <ErrorMessage
                                name="organizationName"
                                component="span"
                                className="text-danger mt-1 d-block small"
                                render={(msg) => typeof msg === "string" ? <span style={{ color: "red" }}>{msg}</span> : null}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        {values.organizationName && values.invoiceItems.length > 0 && (
                          <Row className="g-3">
                            <Col md={12}>
                              <FieldArray name="invoiceItems">
                                {({ push, remove, replace }) => (
                                  <div className="mb-4">
                                    <div className="text-end mb-4">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        type="button"
                                        onClick={() => handleAddItem(values, errors, setFieldTouched, validateField, push)}
                                        className="me-2"
                                      >
                                        {t("AddItem")}
                                      </Button>
                                    </div>
                                    {values.invoiceItems.map((item: InvoiceItem, itemIdx: number) => {
                                      const itemErrors = Array.isArray(errors.invoiceItems) && errors.invoiceItems[itemIdx]
                                        ? errors.invoiceItems[itemIdx] as FormikErrors<InvoiceItem>
                                        : {};
                                      const itemTotal = Number(item.quantity || 0) * Number(item.price || 0) - Number(item.discount || 0);

                                      return (
                                        <div
                                          key={item.id}
                                          className="range-card bg-light border border-1 border-gray rounded-3"
                                        >
                                          {item.isEditing ? (
                                            <EditableItemRow
                                              item={item}
                                              itemIdx={itemIdx}
                                              itemErrors={itemErrors}
                                              setFieldValue={setFieldValue}
                                              handleKeyPress={(e, fieldName) => 
                                                handleKeyPress(e, itemIdx, fieldName, values, errors, setFieldTouched, validateField, push)}
                                              remove={() => remove(itemIdx)}
                                              t={t}
                                            />
                                          ) : (
                                            <DisplayItemRow
                                              item={item}
                                              itemIdx={itemIdx}
                                              replace={replace}
                                              remove={() => remove(itemIdx)}
                                              t={t}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                    <InvoiceSummary totals={totals} t={t} />
                                    <div className="text-end mt-3">
                                      <Button onClick={() => navigate(`${process.env.PUBLIC_URL}/billingpayments/invoice`)} color="secondary" className="me-2">
                                        {t("Cancel")}
                                      </Button>
                                      <Button
                                        color="success"
                                        type="button"
                                        onClick={() => handleSaveAllItems(values, errors, setFieldTouched, validateField, replace)}
                                        className="me-2"
                                      >
                                        {t("Saveasdraft")}
                                      </Button>
                                      <Button color="primary" type="submit">
                                        {t(isEditMode ? "UpdateInvoice" : "SaveInvoice")}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </FieldArray>
                            </Col>
                          </Row>
                        )}
                        <ErrorMessage
                          name="invoiceItems"
                          component="span"
                          className="text-danger mt-1 d-block small"
                          render={(msg) => typeof msg === "string" ? <span style={{ color: "red" }}>{msg}</span> : null}
                        />
                      </Form>
                    );
                  }}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddInvoiceSection;